import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, Car, Anchor, Shield, AlertTriangle, HelpCircle, DollarSign, MapPin, Search, Home, BookOpen, Calculator } from 'lucide-react';

const NotFoundPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Add noindex meta tag for 404 pages
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, follow';
    document.head.appendChild(metaRobots);
    
    document.title = 'Page Not Found | Amy C. Brown Law';
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to blog with search query
      navigate(`/blog?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const quickLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/car-crashes', label: 'Car Accidents', icon: Car },
    { to: '/maritime-injuries', label: 'Maritime Injuries', icon: Anchor },
    { to: '/first-responder-crashes', label: 'First Responders', icon: Shield },
    { to: '/slip-and-falls', label: 'Slip & Falls', icon: AlertTriangle },
    { to: '/compensation-estimator', label: 'Case Calculator', icon: Calculator },
    { to: '/blog', label: 'Learning Center', icon: BookOpen },
    { to: '/faqs', label: 'FAQs', icon: HelpCircle },
    { to: '/pricing', label: 'Pricing & Costs', icon: DollarSign },
    { to: '/areas-we-serve', label: 'Areas I Serve', icon: MapPin },
    { to: '/legal-glossary', label: 'Legal Glossary', icon: BookOpen },
    { to: '/about', label: 'About Amy', icon: null },
  ];

  return (
    <div className="min-h-screen pt-20 bg-accent-soft" data-testid="not-found-page">
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Friendly Message */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
            This page was moved, so let's get you going in the right direction.
          </h1>
          <p className="text-lg text-slate-600 mb-6">
            Here's what I can help you with:
          </p>
          
          {/* Search Box */}
          <form onSubmit={handleSearch} className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              />
              <button 
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-teal-600 text-white px-4 py-1.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Quick Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {quickLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all hover:-translate-y-1 group"
            >
              {link.icon && (
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <link.icon className="w-6 h-6 text-primary" />
                </div>
              )}
              {!link.icon && (
                <div className="w-12 h-12 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-teal-500/20 transition-colors">
                  <span className="text-teal-600 font-bold text-lg">A</span>
                </div>
              )}
              <span className="text-secondary font-medium text-sm">{link.label}</span>
            </Link>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
          <h2 className="font-heading text-xl font-bold text-secondary mb-2">
            Not sure where to start?
          </h2>
          <p className="text-slate-600 mb-6">
            Give me a call or text — I'm happy to point you in the right direction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-hover transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
